import {
  U8_MAX,
  U16_MAX,
  U32_MAX,
  ValidationContext,
  deepFreeze,
  fieldPath,
  isRecord,
  parseItemQuantity,
  parseNumericValue,
  parseOperationProvenance,
  parseRate,
} from "./calculator-user-layer-v4-values.js";
import {
  canonicalizeCalculatorFishingSourceV1,
} from "./components/calculator-fishing-source.js";
import { CALCULATOR_GROUP_ROLES_V1 } from "./calculator-group-role-v1.js";

const PRESENCE_VALUES = new Set(["present", "absent", "unknown"]);
const MEMBERSHIP_VALUES = new Set(["member", "notMember", "unknown"]);
const GROUP_ROLE_VALUES = new Set(CALCULATOR_GROUP_ROLES_V1);
const CONDITION_SUBJECTS = new Set([
  "mastery",
  "lifeskillLevel",
  "fishingLevel",
]);
const COMPARISONS = new Set([
  "equal",
  "notEqual",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
]);
const EFFECT_KINDS = new Set([
  "autoFishingTimeReduction",
  "rareGroupBonus",
  "highQualityGroupBonus",
  "durabilityReductionResistance",
  "fishMultiplier",
  "fishingExperienceBonus",
  "lifeExperienceBonus",
]);
const PET_OPTION_KINDS = new Set(["special", "talent", "learnedSkill"]);

function parseConditions(value, path, context, { required = false } = {}) {
  if (value === undefined && !required) {
    return undefined;
  }
  const conditions = context
    .array(value, path)
    .map((condition, index) => parseCondition(
      condition,
      `${path}[${index}]`,
      context,
    ));
  return required || conditions.length > 0 ? conditions : undefined;
}

function parseFishingSource(value, path, context) {
  return canonicalizeCalculatorFishingSourceV1(
    value,
    path,
    (issuePath, code, message) => context.issue(issuePath, code, message),
  );
}

function parseRateAlternative(value, path, context) {
  if (value === undefined || value === null) {
    return undefined;
  }
  const object = context.exactObject(value, path, [
    "familyId",
    "alternativeId",
    "priority",
    "applicability",
  ]);
  const applicabilityPath = fieldPath(path, "applicability");
  const applicabilityKind = isRecord(object.applicability)
    ? object.applicability.kind
    : undefined;
  let applicability;
  if (applicabilityKind === "fallback") {
    context.exactObject(object.applicability, applicabilityPath, ["kind"]);
    applicability = { kind: "fallback" };
  } else if (applicabilityKind === "typedConditions") {
    const typed = context.exactObject(
      object.applicability,
      applicabilityPath,
      ["kind", "conditions"],
    );
    const conditions = parseConditions(
      typed.conditions,
      fieldPath(applicabilityPath, "conditions"),
      context,
      { required: true },
    );
    if (conditions.length === 0) {
      context.issue(
        fieldPath(applicabilityPath, "conditions"),
        "invalidLength",
        "expected at least one condition",
      );
    }
    applicability = { kind: "typedConditions", conditions };
  } else if (applicabilityKind === "sourceExpression") {
    const source = context.exactObject(
      object.applicability,
      applicabilityPath,
      ["kind", "expression"],
    );
    applicability = {
      kind: "sourceExpression",
      expression: context.string(
        source.expression,
        fieldPath(applicabilityPath, "expression"),
      ),
    };
  } else {
    context.exactObject(object.applicability, applicabilityPath, ["kind"]);
    context.issue(
      fieldPath(applicabilityPath, "kind"),
      "unsupportedValue",
      "expected fallback, typedConditions, or sourceExpression",
    );
    applicability = { kind: "fallback" };
  }
  return {
    familyId: context.string(object.familyId, fieldPath(path, "familyId"), {
      opaqueId: true,
    }),
    alternativeId: context.string(
      object.alternativeId,
      fieldPath(path, "alternativeId"),
      { opaqueId: true },
    ),
    priority: context.integer(
      object.priority,
      fieldPath(path, "priority"),
      0,
      U32_MAX,
    ),
    applicability,
  };
}

function parseCondition(value, path, context) {
  const discriminator = isRecord(value) ? value.kind : undefined;
  if (discriminator === "numeric") {
    const object = context.exactObject(value, path, [
      "kind",
      "subject",
      "comparison",
      "value",
    ]);
    return {
      kind: "numeric",
      subject: context.enum(
        object.subject,
        fieldPath(path, "subject"),
        CONDITION_SUBJECTS,
      ),
      comparison: context.enum(
        object.comparison,
        fieldPath(path, "comparison"),
        COMPARISONS,
      ),
      value: context.finiteNumber(object.value, fieldPath(path, "value"), true),
    };
  }
  context.exactObject(value, path, ["kind"]);
  context.issue(
    fieldPath(path, "kind"),
    "unsupportedValue",
    "expected numeric",
  );
  return { kind: "numeric", subject: "", comparison: "", value: 0 };
}

function parseEffectSubject(value, path, context) {
  const discriminator = isRecord(value) ? value.scope : undefined;
  if (discriminator === "item") {
    const object = context.exactObject(value, path, ["scope", "itemId"]);
    return {
      scope: "item",
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
    };
  }
  if (discriminator === "option") {
    const object = context.exactObject(value, path, ["scope", "optionId"]);
    return {
      scope: "option",
      optionId: context.string(object.optionId, fieldPath(path, "optionId"), {
        opaqueId: true,
      }),
    };
  }
  context.exactObject(value, path, ["scope"]);
  context.issue(
    fieldPath(path, "scope"),
    "unsupportedValue",
    "expected item or option",
  );
  return { scope: "item", itemId: 1 };
}

function parsePetEffect(value, path, context) {
  const object = context.exactObject(value, path, ["effect", "value"]);
  return {
    effect: context.enum(object.effect, fieldPath(path, "effect"), EFFECT_KINDS),
    value: parseNumericValue(object.value, fieldPath(path, "value"), context),
  };
}

function parseTradeFact(value, path, context) {
  const discriminator = isRecord(value) ? value.kind : undefined;
  if (discriminator === "origin" || discriminator === "destination") {
    const idField = discriminator === "origin" ? "originId" : "destinationId";
    const object = context.exactObject(value, path, [
      "kind",
      idField,
      "worldX",
      "worldZ",
    ]);
    return {
      kind: discriminator,
      [idField]: context.string(object[idField], fieldPath(path, idField), {
        opaqueId: true,
      }),
      worldX: context.finiteNumber(object.worldX, fieldPath(path, "worldX")),
      worldZ: context.finiteNumber(object.worldZ, fieldPath(path, "worldZ")),
    };
  }
  if (discriminator === "zoneOriginAffinity") {
    const object = context.exactObject(value, path, [
      "kind",
      "zoneId",
      "originId",
      "pixelCount",
    ]);
    return {
      kind: "zoneOriginAffinity",
      zoneId: context.string(object.zoneId, fieldPath(path, "zoneId"), {
        opaqueId: true,
      }),
      originId: context.string(object.originId, fieldPath(path, "originId"), {
        opaqueId: true,
      }),
      pixelCount: context.integer(
        object.pixelCount,
        fieldPath(path, "pixelCount"),
        0,
        U32_MAX,
      ),
    };
  }
  if (discriminator === "bargainBonus") {
    const object = context.exactObject(value, path, [
      "kind",
      "tradeLevel",
      "rate",
    ]);
    return {
      kind: "bargainBonus",
      tradeLevel: context.integer(
        object.tradeLevel,
        fieldPath(path, "tradeLevel"),
        0,
        U16_MAX,
      ),
      rate: parseRate(object.rate, fieldPath(path, "rate"), context),
    };
  }
  context.exactObject(value, path, ["kind"]);
  context.issue(fieldPath(path, "kind"), "unsupportedValue", "unknown trade fact kind");
  return { kind: "origin", originId: "", worldX: 0, worldZ: 0 };
}

function commonFactOperation(object, path, context) {
  return {
    operationId: context.string(
      object.operationId,
      fieldPath(path, "operationId"),
      { opaqueId: true },
    ),
    factId: context.factId(object.factId, fieldPath(path, "factId")),
    provenance: parseOperationProvenance(
      object.provenance,
      fieldPath(path, "provenance"),
      context,
    ),
  };
}

function exactFactOperation(value, path, context, fields) {
  return context.exactObject(value, path, [
    "kind",
    "operationId",
    "factId",
    "provenance",
    ...fields,
  ]);
}

function parseUserLootRate(value, path, context) {
  const rate = parseRate(value, path, context);
  if (rate.state === "known") {
    if (
      rate.basis !== "rawWeight"
      && rate.basis !== "denormalizedWeight"
      && rate.basis !== "percent"
    ) {
      context.issue(
        fieldPath(path, "basis"),
        "unsupportedValue",
        "known user loot rates must use rawWeight, denormalizedWeight, or percent basis",
      );
    }
    if (rate.basis === "percent" && rate.value > 100) {
      context.issue(
        fieldPath(path, "value"),
        "unsupportedValue",
        "effective percentage must be at most 100",
      );
    }
  }
  return rate;
}

export function canonicalizeCalculatorUserLayerOperationV4(value, path, context) {
  const kind = isRecord(value) ? value.kind : undefined;
  if (kind === "groupMembership") {
    const object = exactFactOperation(value, path, context, [
      "fishingSource",
      "itemId",
      "groupId",
      "membership",
      "conditions",
    ]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      fishingSource: parseFishingSource(
        object.fishingSource,
        fieldPath(path, "fishingSource"),
        context,
      ),
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
      groupId: context.string(object.groupId, fieldPath(path, "groupId"), { opaqueId: true }),
      membership: context.enum(
        object.membership,
        fieldPath(path, "membership"),
        MEMBERSHIP_VALUES,
      ),
      conditions: parseConditions(
        object.conditions,
        fieldPath(path, "conditions"),
        context,
        { required: true },
      ),
    };
  }
  if (kind === "groupPresence") {
    const object = exactFactOperation(value, path, context, [
      "fishingSource",
      "groupId",
      "role",
      "presence",
      "conditions",
    ]);
    const conditions = parseConditions(
      object.conditions,
      fieldPath(path, "conditions"),
      context,
    );
    return {
      kind,
      ...commonFactOperation(object, path, context),
      fishingSource: parseFishingSource(
        object.fishingSource,
        fieldPath(path, "fishingSource"),
        context,
      ),
      groupId: context.string(object.groupId, fieldPath(path, "groupId"), {
        opaqueId: true,
      }),
      role: context.enum(object.role, fieldPath(path, "role"), GROUP_ROLE_VALUES),
      presence: context.enum(
        object.presence,
        fieldPath(path, "presence"),
        PRESENCE_VALUES,
      ),
      ...(conditions === undefined ? {} : { conditions }),
    };
  }
  if (kind === "groupRate") {
    const object = exactFactOperation(value, path, context, [
      "fishingSource",
      "groupId",
      "rate",
      "conditions",
      "alternative",
      "branchId",
    ]);
    const conditions = parseConditions(
      object.conditions,
      fieldPath(path, "conditions"),
      context,
      { required: true },
    );
    const alternative = parseRateAlternative(
      object.alternative,
      fieldPath(path, "alternative"),
      context,
    );
    const rate = parseUserLootRate(object.rate, fieldPath(path, "rate"), context);
    const branchId = context.optionalString(object.branchId, fieldPath(path, "branchId"), {
      opaqueId: true,
    });
    if (alternative !== undefined && branchId === null) {
      context.issue(
        fieldPath(path, "branchId"),
        "missingField",
        "rate alternatives require a non-null branch ID",
      );
    }
    if (alternative !== undefined && (conditions?.length ?? 0) > 0) {
      context.issue(
        fieldPath(path, "conditions"),
        "conflictingField",
        "rate alternative applicability owns conditions; direct conditions must be empty",
      );
    }
    if (
      rate.state === "known"
      && rate.basis === "percent"
      && branchId !== null
    ) {
      context.issue(
        fieldPath(path, "branchId"),
        "unsupportedValue",
        "known percent user loot rates must be branchless",
      );
    }
    return {
      kind,
      ...commonFactOperation(object, path, context),
      fishingSource: parseFishingSource(
        object.fishingSource,
        fieldPath(path, "fishingSource"),
        context,
      ),
      groupId: context.string(object.groupId, fieldPath(path, "groupId"), { opaqueId: true }),
      rate,
      conditions,
      ...(alternative === undefined ? {} : { alternative }),
      branchId,
    };
  }
  if (kind === "itemPresence") {
    const object = exactFactOperation(value, path, context, [
      "fishingSource",
      "itemId",
      "presence",
      "conditions",
    ]);
    const conditions = parseConditions(
      object.conditions,
      fieldPath(path, "conditions"),
      context,
    );
    return {
      kind,
      ...commonFactOperation(object, path, context),
      fishingSource: parseFishingSource(
        object.fishingSource,
        fieldPath(path, "fishingSource"),
        context,
      ),
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
      presence: context.enum(
        object.presence,
        fieldPath(path, "presence"),
        PRESENCE_VALUES,
      ),
      ...(conditions === undefined ? {} : { conditions }),
    };
  }
  if (kind === "itemRate") {
    const object = exactFactOperation(value, path, context, [
      "fishingSource",
      "itemId",
      "groupId",
      "rate",
      "conditions",
      "alternative",
      "branchId",
    ]);
    const conditions = parseConditions(
      object.conditions,
      fieldPath(path, "conditions"),
      context,
    );
    const alternative = parseRateAlternative(
      object.alternative,
      fieldPath(path, "alternative"),
      context,
    );
    const rate = parseUserLootRate(object.rate, fieldPath(path, "rate"), context);
    const branchId = context.optionalString(object.branchId, fieldPath(path, "branchId"), {
      opaqueId: true,
    });
    if (alternative !== undefined && branchId === null) {
      context.issue(
        fieldPath(path, "branchId"),
        "missingField",
        "rate alternatives require a non-null branch ID",
      );
    }
    if (alternative !== undefined && (conditions?.length ?? 0) > 0) {
      context.issue(
        fieldPath(path, "conditions"),
        "conflictingField",
        "rate alternative applicability owns conditions; direct conditions must be empty",
      );
    }
    if (
      rate.state === "known"
      && rate.basis === "percent"
      && branchId !== null
    ) {
      context.issue(
        fieldPath(path, "branchId"),
        "unsupportedValue",
        "known percent user loot rates must be branchless",
      );
    }
    return {
      kind,
      ...commonFactOperation(object, path, context),
      fishingSource: parseFishingSource(
        object.fishingSource,
        fieldPath(path, "fishingSource"),
        context,
      ),
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
      groupId: context.string(object.groupId, fieldPath(path, "groupId"), { opaqueId: true }),
      rate,
      ...(conditions === undefined ? {} : { conditions }),
      ...(alternative === undefined ? {} : { alternative }),
      branchId,
    };
  }
  if (kind === "itemQuantity") {
    const object = exactFactOperation(value, path, context, [
      "fishingSource",
      "itemId",
      "groupId",
      "quantity",
    ]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      fishingSource: parseFishingSource(
        object.fishingSource,
        fieldPath(path, "fishingSource"),
        context,
      ),
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
      groupId: context.string(object.groupId, fieldPath(path, "groupId"), { opaqueId: true }),
      quantity: parseItemQuantity(
        object.quantity,
        fieldPath(path, "quantity"),
        context,
      ),
    };
  }
  if (kind === "itemQuantityDefault") {
    const object = exactFactOperation(value, path, context, [
      "itemId",
      "quantity",
    ]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
      quantity: parseItemQuantity(
        object.quantity,
        fieldPath(path, "quantity"),
        context,
      ),
    };
  }
  if (kind === "itemPrice") {
    const object = exactFactOperation(value, path, context, ["itemId", "price"]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
      price: parseNumericValue(object.price, fieldPath(path, "price"), context),
    };
  }
  if (kind === "tradeCurve") {
    const object = exactFactOperation(value, path, context, ["itemId", "pricePercent"]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      itemId: context.integer(object.itemId, fieldPath(path, "itemId"), 1, U32_MAX),
      pricePercent: parseRate(
        object.pricePercent,
        fieldPath(path, "pricePercent"),
        context,
      ),
    };
  }
  if (kind === "itemEffect") {
    const object = exactFactOperation(value, path, context, ["subject", "effect", "value"]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      subject: parseEffectSubject(object.subject, fieldPath(path, "subject"), context),
      effect: context.enum(object.effect, fieldPath(path, "effect"), EFFECT_KINDS),
      value: parseNumericValue(object.value, fieldPath(path, "value"), context),
    };
  }
  if (kind === "petOption") {
    const object = exactFactOperation(value, path, context, [
      "petId",
      "tier",
      "optionId",
      "optionKind",
      "learnChance",
      "effects",
    ]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      petId: context.string(object.petId, fieldPath(path, "petId"), { opaqueId: true }),
      tier: context.integer(object.tier, fieldPath(path, "tier"), 0, U8_MAX),
      optionId: context.string(object.optionId, fieldPath(path, "optionId"), { opaqueId: true }),
      optionKind: context.enum(
        object.optionKind,
        fieldPath(path, "optionKind"),
        PET_OPTION_KINDS,
      ),
      learnChance: parseRate(
        object.learnChance,
        fieldPath(path, "learnChance"),
        context,
      ),
      effects: context
        .array(object.effects, fieldPath(path, "effects"))
        .map((effect, index) => parsePetEffect(
          effect,
          `${fieldPath(path, "effects")}[${index}]`,
          context,
        )),
    };
  }
  if (kind === "tradeFact") {
    const object = exactFactOperation(value, path, context, ["fact"]);
    return {
      kind,
      ...commonFactOperation(object, path, context),
      fact: parseTradeFact(object.fact, fieldPath(path, "fact"), context),
    };
  }
  if (kind === "refuteEvidence") {
    const object = context.exactObject(value, path, [
      "kind",
      "operationId",
      "provenance",
      "targetFactId",
      "reasonCode",
    ]);
    return {
      kind,
      operationId: context.string(
        object.operationId,
        fieldPath(path, "operationId"),
        { opaqueId: true },
      ),
      provenance: parseOperationProvenance(
        object.provenance,
        fieldPath(path, "provenance"),
        context,
      ),
      targetFactId: context.factId(
        object.targetFactId,
        fieldPath(path, "targetFactId"),
      ),
      reasonCode: context.string(
        object.reasonCode,
        fieldPath(path, "reasonCode"),
        { opaqueId: true },
      ),
    };
  }

  context.exactObject(value, path, ["kind"]);
  context.issue(
    fieldPath(path, "kind"),
    "unsupportedOperationKind",
    "unknown or non-editable user operation kind",
  );
  return { kind: "" };
}

export function parseCalculatorUserLayerOperationV4(value) {
  const context = new ValidationContext();
  const operation = canonicalizeCalculatorUserLayerOperationV4(value, "$", context);
  context.finish();
  return deepFreeze(operation);
}

export function parseCalculatorUserLayerOperationIdV4(value) {
  const context = new ValidationContext();
  const operationId = context.string(value, "$.operationId", { opaqueId: true });
  context.finish();
  return operationId;
}
